// Tag-driven weak-area drills. Pulls fresh MCQs on the concepts where the student
// is weak (ORANGE/PINK), graded via the existing practice endpoint. Each companion
// MCQ can reveal its enriched "parent" worked solution AFTER it's been attempted.
import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { BinColor } from '@/types';
import { apiGet, apiPost } from '@/lib/api';
import { BIN_FILL } from '@/lib/bins';
import { Button, Card, StudentPage, StudentPageHeader } from '@/components/ds';
import { MathText } from '@/components/exam/MathText';
import { ReportButton } from '@/components/ReportButton';
import { MotivationBanner } from '@/components/MotivationBanner';
import { fontPx } from '@/lib/fontSize';

interface WeakTag {
  tag: string;
  count: number;
}
interface DrillQuestion {
  id: string;
  text?: string;
  options?: string[];
  tags?: string[];
  maxMarks: number;
}
interface DrillStart {
  session: { id: string };
  tags: string[];
  questions: DrillQuestion[];
}
interface Parent {
  id: string;
  text?: string;
  correctAnswer?: string;
  workedExamples?: { step?: string; explanation?: string }[];
}

export function DrillsPage() {
  const navigate = useNavigate();
  const weak = useQuery({
    queryKey: ['drills', 'weak-tags'],
    queryFn: () => apiGet<WeakTag[]>('/drills/weak-tags'),
  });

  const [drill, setDrill] = useState<DrillStart | null>(null);
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState<number | null>(null);
  const [result, setResult] = useState<{ isCorrect: boolean; bin?: BinColor } | null>(null);
  const [parent, setParent] = useState<Parent | null>(null);
  const [parentLoading, setParentLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const startedAt = useRef<number>(Date.now());

  const q = drill?.questions[idx];
  const isCompanion = Boolean(q?.tags?.some((t) => t.startsWith('seed:')));

  async function startDrill() {
    setStarting(true);
    try {
      const d = await apiPost<DrillStart>('/drills/start', {});
      setDrill(d);
      setIdx(0);
      setPicked(null);
      setResult(null);
      setParent(null);
      startedAt.current = Date.now();
    } finally {
      setStarting(false);
    }
  }

  async function submit(optionIdx: number) {
    if (!drill || !q || result) return;
    setPicked(optionIdx);
    const secondsTaken = Math.round((Date.now() - startedAt.current) / 1000);
    const r = await apiPost<{ isCorrect: boolean; bin?: BinColor }>(
      `/practice/sessions/${drill.session.id}/attempts`,
      { questionId: q.id, answer: String(optionIdx), answerMethod: 'OPTION', secondsTaken },
    );
    setResult(r);
  }

  async function showParent() {
    if (!q) return;
    setParentLoading(true);
    try {
      const r = await apiGet<{ parent: Parent | null }>(`/questions/${q.id}/parent`);
      setParent(r.parent);
    } finally {
      setParentLoading(false);
    }
  }

  function next() {
    if (!drill) return;
    setPicked(null);
    setResult(null);
    setParent(null);
    startedAt.current = Date.now();
    setIdx((i) => i + 1);
  }

  function leaveDrill() {
    setDrill(null);
    setIdx(0);
    setPicked(null);
    setResult(null);
    setParent(null);
  }

  // ── Runner ──
  if (drill) {
    if (idx >= drill.questions.length) {
      return (
        <StudentPage className="gap-4">
          <StudentPageHeader
            breadcrumbs={[
              { label: 'Weak areas', onClick: leaveDrill },
              { label: 'Drill complete' },
            ]}
            title="Drill complete 💪"
            subtitle="Nice work on your weak spots. Run another focused set when you're ready."
          />
          <div>
            <Button disabled={starting} onClick={startDrill}>
              Another drill →
            </Button>
          </div>
        </StudentPage>
      );
    }
    return (
      <StudentPage className="gap-4">
        <StudentPageHeader
          breadcrumbs={[
            { label: 'Weak areas', onClick: leaveDrill },
            { label: 'Drill' },
            { label: `Question ${idx + 1}` },
          ]}
          title={`Question ${idx + 1} of ${drill.questions.length}`}
          subtitle={`Drilling: ${drill.tags.slice(0, 3).join(', ')}`}
          action={q ? <ReportButton questionId={q.id} /> : undefined}
        />
        <div className="flex items-center justify-between text-xs text-ink-soft" data-numeric="">
          <span>Choose the best answer.</span>
        </div>
        <Card>
          <div className="flex flex-col gap-4 p-1">
            <div className="text-lg leading-relaxed text-ink">
              <MathText>{q?.text}</MathText>
            </div>
            <div className="flex flex-col gap-2">
              {q?.options?.map((opt, i) => {
                const isPicked = picked === i;
                const reveal = result != null;
                return (
                  <button
                    key={i}
                    disabled={reveal}
                    onClick={() => submit(i)}
                    className="rounded-card border-2 px-4 py-3 text-left transition"
                    style={{
                      borderColor: isPicked ? 'var(--brand-forest)' : 'var(--surface-sunk)',
                      background: isPicked ? 'var(--green-bg)' : 'var(--surface-card,#fff)',
                      opacity: reveal && !isPicked ? 0.6 : 1,
                    }}
                  >
                    <MathText>{opt}</MathText>
                  </button>
                );
              })}
            </div>

            {result && (
              <div
                className="rounded-2xl p-4 text-center text-white"
                style={{
                  backgroundColor: BIN_FILL[result.bin ?? (result.isCorrect ? 'GREEN' : 'PINK')],
                }}
              >
                <p className="text-base font-bold">
                  {result.isCorrect ? 'Correct!' : 'Not quite — review and retry later'}
                </p>
              </div>
            )}

            {result && !result.isCorrect && q && (
              <MotivationBanner attemptIndex={2} questionId={q.id} />
            )}

            {result && isCompanion && !parent && (
              <Button variant="ghost" disabled={parentLoading} onClick={showParent}>
                {parentLoading ? 'Loading…' : 'See the full worked solution →'}
              </Button>
            )}

            {parent && (
              <div className="rounded-card border border-line p-4">
                <div className="mb-2 text-sm font-bold text-forest">Worked solution</div>
                <div className="text-sm text-ink">
                  <MathText>{parent.text}</MathText>
                </div>
                {parent.workedExamples?.map((w, i) => (
                  <p key={i} className="mt-2 text-sm text-ink-soft">
                    <MathText>{w.explanation ?? ''}</MathText>
                  </p>
                ))}
                {parent.correctAnswer && (
                  <p className="mt-2 text-sm text-ink">
                    <span className="font-semibold">Answer: </span>
                    <MathText>{parent.correctAnswer}</MathText>
                  </p>
                )}
              </div>
            )}

            {result && (
              <div className="flex justify-end">
                <Button onClick={next}>
                  {idx + 1 < drill.questions.length ? 'Next question →' : 'Finish'}
                </Button>
              </div>
            )}
          </div>
        </Card>
      </StudentPage>
    );
  }

  // ── Picker ──
  return (
    <StudentPage>
      <StudentPageHeader
        title="Drill your weak spots"
        subtitle="Fresh questions on the concepts you’ve been missing — quick, focused practice."
      />

      {weak.isLoading && <p className="text-ink-soft">Finding your weak areas…</p>}

      {weak.data && weak.data.length === 0 && (
        <Card>
          <div className="flex flex-col items-start gap-3 p-1">
            <p className="text-ink">
              No weak areas yet — take a paper or the diagnostic and we’ll spot the concepts to
              drill.
            </p>
            <div className="flex gap-3">
              <Button onClick={() => navigate('/tests/diagnostic')}>Take the diagnostic →</Button>
              <Button variant="ghost" onClick={() => navigate('/tests/past-papers')}>
                Browse papers
              </Button>
            </div>
          </div>
        </Card>
      )}

      {weak.data && weak.data.length > 0 && (
        <>
          <div className="flex flex-wrap gap-2">
            {weak.data.slice(0, 12).map((w) => (
              <span
                key={w.tag}
                className="rounded-full px-3 py-1 text-xs font-semibold"
                style={{
                  background: 'var(--orange-bg, #FFF1E0)',
                  color: 'var(--orange-text, #9A5300)',
                }}
                data-numeric=""
              >
                {w.tag} · {w.count}
              </span>
            ))}
          </div>
          <div>
            <Button
              style={{ padding: '12px 22px', fontSize: fontPx(15) }}
              disabled={starting}
              onClick={startDrill}
            >
              {starting ? 'Building your drill…' : 'Start drill →'}
            </Button>
          </div>
        </>
      )}
    </StudentPage>
  );
}
